import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Barcode, Camera, Check, Search } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { TextField } from '@/components/Field';
import { guardarAlimento } from '@/db/alimentos';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import {
  alimentoDesdeProducto,
  textoDeFaltantes,
  validarCodigo,
  TEXTO_CODIGO_INVALIDO,
  type BorradorDeProducto,
} from '@/domain/codigo-barras';
import { formatearGramos, formatearKcal } from '@/domain/nutricion';
import { buscarPorCodigo, TEXTO_BUSQUEDA } from '@/lib/openfoodfacts';
import {
  abrirCamara,
  cerrarCamara,
  crearLector,
  soporteDeEscaner,
  TEXTO_ERROR_CAMARA,
  type ErrorDeCamara,
} from '@/lib/escaner';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Etiqueta } from './comunes';

type Estado =
  | { tipo: 'inicio' }
  | { tipo: 'buscando' }
  | { tipo: 'listo'; borrador: BorradorDeProducto }
  | { tipo: 'aviso'; titulo: string; texto: string };

/**
 * Buscar un producto por su código de barras (Fase 6, 0.13.0; Req. 2.9, 20.7).
 *
 * Es la única pantalla de DuoFit que usa internet, y lo dice. Lo que sale del
 * teléfono es solo el código de barras: ni comidas, ni pesajes, ni medidas, ni
 * historial. Lo que llega se muestra para revisarlo y **no se guarda solo**;
 * los macros que el producto no trae quedan en blanco, no en cero.
 */
export function CodigoDeBarrasPage() {
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const volverA = parametros.get('volver') ?? '/comida/mis-alimentos';

  const [codigo, setCodigo] = useState('');
  const [errorCodigo, setErrorCodigo] = useState<string | undefined>(undefined);
  const [estado, setEstado] = useState<Estado>({ tipo: 'inicio' });
  const [guardando, setGuardando] = useState(false);
  const soporte = soporteDeEscaner();

  async function buscar(texto: string) {
    const r = validarCodigo(texto);
    if (!r.valido) {
      setErrorCodigo(TEXTO_CODIGO_INVALIDO[r.motivo]);
      return;
    }
    setErrorCodigo(undefined);
    setCodigo(r.codigo);
    setEstado({ tipo: 'buscando' });

    const busqueda = await buscarPorCodigo(r.codigo);
    if (busqueda.tipo === 'sin_conexion') {
      setEstado({ tipo: 'aviso', titulo: 'Sin internet', texto: TEXTO_BUSQUEDA.sin_conexion });
      return;
    }
    if (busqueda.tipo === 'tardo') {
      setEstado({ tipo: 'aviso', titulo: 'No respondió', texto: TEXTO_BUSQUEDA.tardo });
      return;
    }
    if (busqueda.tipo === 'error') {
      setEstado({ tipo: 'aviso', titulo: 'No se pudo buscar', texto: busqueda.detalle });
      return;
    }
    switch (busqueda.lectura.tipo) {
      case 'listo':
        setEstado({ tipo: 'listo', borrador: busqueda.lectura.borrador });
        return;
      case 'sin_producto':
        setEstado({
          tipo: 'aviso',
          titulo: 'Ese código no está en Open Food Facts',
          texto: 'Nadie lo ha capturado todavía. Puedes crear el alimento a mano desde su etiqueta.',
        });
        return;
      case 'sin_valores':
        setEstado({
          tipo: 'aviso',
          titulo: 'El producto está, pero sin valores',
          texto: `${busqueda.lectura.nombre ?? 'Ese producto'} no trae las calorías por 100 g, así que no hay nada que registrar. Captúralo a mano desde su etiqueta.`,
        });
        return;
      case 'sin_nombre':
        setEstado({
          tipo: 'aviso',
          titulo: 'El producto no tiene nombre',
          texto: 'La ficha de Open Food Facts está incompleta. Captúralo a mano desde su etiqueta.',
        });
        return;
    }
  }

  async function guardar(borrador: BorradorDeProducto) {
    if (guardando) return;
    setGuardando(true);
    try {
      const alimento = alimentoDesdeProducto(borrador, nuevoId('al'), hoy, new Date().toISOString());
      const guardado = await guardarAlimento(alimento);
      // Se abre el alimento guardado, para ajustar lo que falte con la etiqueta
      void navegar(`/comida/mis-alimentos/${guardado.id}`, { replace: true });
    } catch (e) {
      setEstado({ tipo: 'aviso', titulo: 'No se pudo guardar', texto: mensajeDeError(e) });
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        compacto
        titulo="Código de barras"
        descripcion="Busca un producto empaquetado en Open Food Facts y revísalo antes de guardarlo."
        volver={{ a: volverA, texto: 'Volver' }}
      />

      <Banner tono="info" titulo="Esto sí usa internet">
        <p>
          Es lo único de DuoFit que consulta la red. Lo que sale de tu teléfono es solo el código de
          barras: ni tus comidas, ni tus pesajes, ni tus medidas, ni tu historial.
        </p>
        <p>
          DuoFit <strong className="font-medium text-texto">no lee la etiqueta nutrimental</strong>:
          lee el código y consulta lo que la comunidad de Open Food Facts capturó de ese producto.
          Por eso los valores se guardan como referencia y no como la etiqueta de tu producto.
        </p>
      </Banner>

      {soporte !== 'sin_camara' && estado.tipo !== 'listo' && (
        <Escaner
          avisarWasm={soporte === 'wasm'}
          onCodigo={(c) => void buscar(c)}
          onError={(motivo) => setEstado({ tipo: 'aviso', titulo: 'Cámara', texto: TEXTO_ERROR_CAMARA[motivo] })}
        />
      )}

      {estado.tipo !== 'listo' && (
        <Card>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void buscar(codigo);
            }}
          >
            <TextField
              etiqueta="Código de barras"
              valor={codigo}
              onCambio={(v) => {
                setCodigo(v);
                setErrorCodigo(undefined);
              }}
              error={errorCodigo}
              placeholder="7501055363513"
              ayuda="Los dígitos debajo de las barras. Se revisa antes de buscarlo."
              maxLength={20}
            />
            <Button type="submit" icono={Search} disabled={estado.tipo === 'buscando'}>
              {estado.tipo === 'buscando' ? 'Buscando…' : 'Buscar en internet'}
            </Button>
          </form>
        </Card>
      )}

      {estado.tipo === 'aviso' && (
        <Banner tono="aviso" titulo={estado.titulo}>
          {estado.texto}
        </Banner>
      )}

      {estado.tipo === 'listo' && (
        <Producto
          borrador={estado.borrador}
          guardando={guardando}
          onGuardar={() => void guardar(estado.borrador)}
          onOtro={() => {
            setCodigo('');
            setEstado({ tipo: 'inicio' });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Lo que trae el producto, para revisarlo antes de guardar. */
function Producto({
  borrador,
  guardando,
  onGuardar,
  onOtro,
}: {
  borrador: BorradorDeProducto;
  guardando: boolean;
  onGuardar: () => void;
  onOtro: () => void;
}) {
  const faltantes = textoDeFaltantes(borrador.faltantes);
  const n = borrador.nutrientes;
  const macro = (nombre: string, valor: number | null) => (
    <div className="min-w-0">
      <dt className="text-[12px] text-texto-suave">{nombre}</dt>
      <dd className="text-[15px] font-medium tabular-nums text-texto">
        {valor === null ? <span className="text-aviso">Sin dato</span> : formatearGramos(valor)}
      </dd>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <span className="antetitulo">Esto trae el producto</span>
        <h2 className="mt-2 text-[22px] leading-tight tracking-[-0.6px] text-texto">{borrador.nombre}</h2>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-texto-suave">
          <Barcode aria-hidden="true" className="size-4" />
          <span className="tabular-nums">{borrador.codigo}</span>
          {borrador.contenido !== null && <span>· {borrador.contenido}</span>}
          <Etiqueta tono="aviso">Referencia</Etiqueta>
        </p>

        <p className="mt-4 text-[30px] leading-none tracking-[-1px] tabular-nums text-texto">
          {formatearKcal(n.kcal)}
          <span className="text-[14px] text-texto-suave"> por 100 g</span>
        </p>
        {borrador.kcalDesdeKj && (
          <p className="mt-1 text-[12px] text-texto-suave">
            El producto trae los kJ y no las kcal: se calcularon desde ahí.
          </p>
        )}

        <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-borde pt-4 min-[400px]:grid-cols-4">
          {macro('Proteína', n.proteinaG)}
          {macro('Carbohidratos', n.carbohidratosG)}
          {macro('Grasa', n.grasaG)}
          {macro('Fibra', n.fibraG)}
        </dl>

        {borrador.porciones.length > 0 && (
          <p className="mt-3 text-[12px] text-texto-suave">
            Porción de la etiqueta: {borrador.porciones.map((p) => `${p.nombre} (${p.gramos} g)`).join(', ')}
          </p>
        )}
      </Card>

      {faltantes !== null && (
        <Banner tono="aviso" titulo="Faltan datos">
          {faltantes}
        </Banner>
      )}

      <div className="grid gap-2">
        <Button icono={Check} disabled={guardando} onClick={onGuardar}>
          {guardando ? 'Guardando…' : 'Guardar en Mis alimentos'}
        </Button>
        <Button variante="secundario" disabled={guardando} onClick={onOtro}>
          Buscar otro producto
        </Button>
      </div>
      <p className="text-[12px] leading-relaxed text-texto-suave">
        Al guardarlo se abre para que ajustes lo que falte con la etiqueta que tienes en la mano.
        Nada se registra en tu día todavía.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * La cámara buscando el código. Se abre con un toque (Safari no la concede de
 * otra forma) y se apaga al salir de la pantalla.
 */
function Escaner({
  avisarWasm,
  onCodigo,
  onError,
}: {
  avisarWasm: boolean;
  onCodigo: (codigo: string) => void;
  onError: (motivo: ErrorDeCamara) => void;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const flujo = useRef<MediaStream | null>(null);
  const corriendo = useRef(false);
  const [mirando, setMirando] = useState(false);
  const [cargando, setCargando] = useState(false);

  // Al salir de la pantalla, la cámara se apaga
  useEffect(
    () => () => {
      corriendo.current = false;
      cerrarCamara(flujo.current);
      flujo.current = null;
    },
    [],
  );

  async function empezar() {
    setCargando(true);
    const camara = await abrirCamara();
    if (camara.tipo === 'error') {
      setCargando(false);
      onError(camara.motivo);
      return;
    }
    flujo.current = camara.flujo;
    setMirando(true);
    const elemento = video.current;
    if (elemento !== null) {
      elemento.srcObject = camara.flujo;
      await elemento.play().catch(() => undefined);
    }

    let lector;
    try {
      lector = await crearLector();
    } catch {
      setCargando(false);
      onError('otro');
      return;
    }
    setCargando(false);

    const lienzo = document.createElement('canvas');
    corriendo.current = true;
    const mirar = async () => {
      if (!corriendo.current) return;
      const el = video.current;
      if (el !== null && el.videoWidth > 0) {
        lienzo.width = el.videoWidth;
        lienzo.height = el.videoHeight;
        const ctx = lienzo.getContext('2d', { willReadFrequently: true });
        if (ctx !== null) {
          ctx.drawImage(el, 0, 0);
          const encontrado = await lector.leer(ctx.getImageData(0, 0, lienzo.width, lienzo.height));
          if (encontrado !== null && corriendo.current) {
            corriendo.current = false;
            cerrarCamara(flujo.current);
            flujo.current = null;
            setMirando(false);
            onCodigo(encontrado);
            return;
          }
        }
      }
      // Unas pocas veces por segundo: más no ayuda y calienta el teléfono
      window.setTimeout(() => void mirar(), 300);
    };
    void mirar();
  }

  return (
    <Card>
      <div className="space-y-3">
        {mirando ? (
          <>
            <div className="overflow-hidden rounded-[15px] bg-superficie-suave">
              <video ref={video} muted playsInline className="block aspect-[4/3] w-full object-cover" />
            </div>
            <p aria-live="polite" className="text-[12px] text-texto-suave">
              {cargando
                ? 'Preparando el lector…'
                : 'Acerca el código de barras hasta que llene el recuadro. Se busca solo al reconocerlo.'}
            </p>
          </>
        ) : (
          <>
            <Button icono={Camera} disabled={cargando} onClick={() => void empezar()}>
              {cargando ? 'Abriendo la cámara…' : 'Escanear con la cámara'}
            </Button>
            <p className="text-[12px] leading-relaxed text-texto-suave">
              {avisarWasm
                ? 'La primera vez se baja el lector de códigos (unos 925 KB) y queda guardado. También puedes escribir el código a mano.'
                : 'También puedes escribir el código a mano.'}
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
